import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  class: "mt-5 pt-5 ps-3",
  "aria-label": "breadcrumb"
}
const _hoisted_2 = { class: "breadcrumb mb-3" }
const _hoisted_3 = { class: "breadcrumb-item" }
const _hoisted_4 = { class: "container-fluid" }
const _hoisted_5 = { class: "row" }
const _hoisted_6 = { class: "col-sm-12" }
const _hoisted_7 = { class: "card" }
const _hoisted_8 = { class: "card-header d-flex justify-content-between align-items-center" }
const _hoisted_9 = { class: "d-flex gap-2" }
const _hoisted_10 = { class: "card-body" }
const _hoisted_11 = { class: "row mb-4" }
const _hoisted_12 = { class: "col-md-4" }
const _hoisted_13 = { class: "input-group" }
const _hoisted_14 = { class: "table-responsive" }
const _hoisted_15 = { class: "table table-hover" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = { key: 1 }
const _hoisted_18 = { key: 2 }
const _hoisted_19 = {
  colspan: 8,
  class: "text-center py-5"
}
const _hoisted_20 = { class: "alert alert-danger" }
const _hoisted_21 = { key: 3 }
const _hoisted_22 = ["src"]
const _hoisted_23 = {
  key: 1,
  class: "rounded bg-secondary text-white d-flex align-items-center justify-content-center",
  style: {"width":"40px","height":"40px"}
}
const _hoisted_24 = ["onClick"]
const _hoisted_25 = { class: "text-muted" }
const _hoisted_26 = { class: "btn-group" }
const _hoisted_27 = ["onClick"]
const _hoisted_28 = ["onClick", "title"]
const _hoisted_29 = ["onClick"]
const _hoisted_30 = {
  key: 0,
  class: "card-footer bg-light"
}
const _hoisted_31 = { class: "d-flex justify-content-between align-items-center" }
const _hoisted_32 = { class: "text-muted small" }
const _hoisted_33 = { class: "pagination-mini" }
const _hoisted_34 = ["disabled"]
const _hoisted_35 = { class: "mx-2 small" }
const _hoisted_36 = ["disabled"]

import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import apiService from '@/services/apiService';
import Swal from 'sweetalert2';

interface Company {
  _id: string;
  title: string;
  slug: string;
  company_size: string;
  logo: string | null;
  status: string;
  is_active: boolean;
  billing_status: string;
  last_activity_at: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CompaniesList',
  setup(__props) {

const router = useRouter();
const companies = ref<Company[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
const searchQuery = ref('');

const pagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false
});

const loadCompanies = async (page = 1) => {
  isLoading.value = true;
  error.value = null;
  try {
    const params = {
      page,
      limit: pagination.value.per_page,
      search: searchQuery.value
    };
    const response = await apiService.getCompanies(params);
    
    // Adapt to standardizeResponse structure from apiService
    if (response.data) {
      companies.value = response.data;
      if (response.pagination) {
        pagination.value = {
          current_page: response.pagination.currentPage || response.pagination.page || page,
          per_page: response.pagination.limit || 10,
          total: response.pagination.totalItems || response.pagination.total || 0,
          total_pages: response.pagination.totalPages || response.pagination.total_pages || 1,
          has_next: (response.pagination.currentPage || page) < (response.pagination.totalPages || 1),
          has_prev: (response.pagination.currentPage || page) > 1
        };
      }
    } else if (Array.isArray(response)) {
      companies.value = response;
      pagination.value.total = response.length;
    }
  } catch (err: any) {
    error.value = err.message || 'Failed to load companies';
    console.error(err);
  } finally {
    isLoading.value = false;
  }
};

const handleSearch = () => {
  pagination.value.current_page = 1;
  loadCompanies(1);
};

const handlePageChange = (page: number) => {
  loadCompanies(page);
};

const viewDetails = (id: string) => {
  router.push(`/admin/companies/${id}`);
};

const handleToggleStatus = async (company: Company) => {
  try {
    await apiService.toggleCompanyStatus(company._id);
    company.is_active = !company.is_active;
    company.status = company.is_active ? 'active' : 'inactive';
    Swal.fire({
      icon: 'success',
      title: 'Status Updated',
      text: `Company is now ${company.status}`,
      timer: 1500,
      showConfirmButton: false
    });
  } catch (err) {
    Swal.fire('Error', 'Failed to update status', 'error');
  }
};

const confirmDelete = async (company: Company) => {
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: `You are about to soft delete ${company.title}.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    confirmButtonText: 'Yes, delete it!'
  });

  if (result.isConfirmed) {
    try {
      await apiService.deleteCompany(company._id);
      Swal.fire('Deleted!', 'Company has been deleted.', 'success');
      loadCompanies(pagination.value.current_page);
    } catch (err) {
      Swal.fire('Error', 'Failed to delete company', 'error');
    }
  }
};

const getStatusBadge = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active': return 'bg-success';
    case 'inactive': return 'bg-warning';
    case 'suspended': return 'bg-danger';
    default: return 'bg-secondary';
  }
};

const getBillingBadge = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
    case 'paid': return 'bg-info text-dark';
    case 'none': return 'bg-light text-dark';
    case 'overdue': return 'bg-danger';
    default: return 'bg-secondary';
  }
};

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

onMounted(() => {
  loadCompanies();
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("nav", _hoisted_1, [
      _createElementVNode("ol", _hoisted_2, [
        _createElementVNode("li", _hoisted_3, [
          _createVNode(_component_router_link, { to: "/" }, {
            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
              _createTextVNode("Home", -1)
            ]))]),
            _: 1
          })
        ]),
        _cache[5] || (_cache[5] = _createElementVNode("li", {
          class: "breadcrumb-item active",
          "aria-current": "page"
        }, "Companies", -1))
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _cache[7] || (_cache[7] = _createElementVNode("h5", null, "Companies Management", -1)),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("button", {
                  class: "btn btn-outline-primary btn-sm",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (loadCompanies()))
                }, [...(_cache[6] || (_cache[6] = [
                  _createElementVNode("i", { class: "fa fa-refresh me-1" }, null, -1),
                  _createTextVNode(" Refresh ", -1)
                ]))])
              ])
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _cache[8] || (_cache[8] = _createElementVNode("span", { class: "input-group-text" }, [
                      _createElementVNode("i", { class: "fa fa-search" })
                    ], -1)),
                    _withDirectives(_createElementVNode("input", {
                      type: "text",
                      class: "form-control",
                      placeholder: "Search by title or slug...",
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
                      onInput: handleSearch
                    }, null, 544), [
                      [_vModelText, searchQuery.value]
                    ])
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_14, [
                _createElementVNode("table", _hoisted_15, [
                  _cache[15] || (_cache[15] = _createElementVNode("thead", { class: "table-dark" }, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", null, "Logo"),
                      _createElementVNode("th", null, "Title"),
                      _createElementVNode("th", null, "Slug"),
                      _createElementVNode("th", null, "Size"),
                      _createElementVNode("th", null, "Status"),
                      _createElementVNode("th", null, "Billing"),
                      _createElementVNode("th", null, "Last Activity"),
                      _createElementVNode("th", null, "Actions")
                    ])
                  ], -1)),
                  (isLoading.value)
                    ? (_openBlock(), _createElementBlock("tbody", _hoisted_16, [...(_cache[9] || (_cache[9] = [
                        _createElementVNode("tr", null, [
                          _createElementVNode("td", {
                            colspan: 8,
                            class: "text-center py-4"
                          }, [
                            _createElementVNode("div", {
                              class: "spinner-border text-primary",
                              role: "status"
                            }, [
                              _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
                            ])
                          ])
                        ], -1)
                      ]))]))
                    : (companies.value.length === 0)
                      ? (_openBlock(), _createElementBlock("tbody", _hoisted_17, [...(_cache[10] || (_cache[10] = [
                          _createElementVNode("tr", null, [
                            _createElementVNode("td", {
                              colspan: 8,
                              class: "text-center py-5"
                            }, [
                              _createElementVNode("div", { class: "mb-3" }, [
                                _createElementVNode("i", { class: "fa fa-building fa-3x text-muted" })
                              ]),
                              _createElementVNode("h5", { class: "text-muted" }, "No Companies Found")
                            ])
                          ], -1)
                        ]))]))
                      : (error.value)
                        ? (_openBlock(), _createElementBlock("tbody", _hoisted_18, [
                            _createElementVNode("tr", null, [
                              _createElementVNode("td", _hoisted_19, [
                                _createElementVNode("div", _hoisted_20, [
                                  _cache[11] || (_cache[11] = _createElementVNode("i", { class: "fa fa-exclamation-triangle me-2" }, null, -1)),
                                  _createTextVNode(_toDisplayString(error.value), 1)
                                ])
                              ])
                            ])
                          ]))
                        : (_openBlock(), _createElementBlock("tbody", _hoisted_21, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(companies.value, (company) => {
                              return (_openBlock(), _createElementBlock("tr", {
                                key: company._id
                              }, [
                                _createElementVNode("td", null, [
                                  (company.logo)
                                    ? (_openBlock(), _createElementBlock("img", {
                                        key: 0,
                                        src: company.logo,
                                        class: "rounded",
                                        style: {"width":"40px","height":"40px","object-fit":"cover"}
                                      }, null, 8, _hoisted_22))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_23, [...(_cache[12] || (_cache[12] = [
                                        _createElementVNode("i", { class: "fa fa-building" }, null, -1)
                                      ]))]))
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("div", {
                                    class: "fw-bold text-primary",
                                    style: {"cursor":"pointer"},
                                    onClick: ($event: any) => (viewDetails(company._id))
                                  }, _toDisplayString(company.title), 9, _hoisted_24)
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("code", null, _toDisplayString(company.slug), 1)
                                ]),
                                _createElementVNode("td", null, _toDisplayString(company.company_size), 1),
                                _createElementVNode("td", null, [
                                  _createElementVNode("span", {
                                    class: _normalizeClass(['badge', getStatusBadge(company.status)])
                                  }, _toDisplayString(company.status), 3)
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("span", {
                                    class: _normalizeClass(['badge', getBillingBadge(company.billing_status)])
                                  }, _toDisplayString(company.billing_status), 3)
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("small", _hoisted_25, _toDisplayString(company.last_activity_at ? formatTimeAgo(company.last_activity_at) : 'Never'), 1)
                                ]),
                                _createElementVNode("td", null, [
                                  _createElementVNode("div", _hoisted_26, [
                                    _createElementVNode("button", {
                                      class: "btn btn-sm btn-outline-info",
                                      onClick: ($event: any) => (viewDetails(company._id)),
                                      title: "View Details"
                                    }, [...(_cache[13] || (_cache[13] = [
                                      _createElementVNode("i", { class: "fa fa-eye" }, null, -1)
                                    ]))], 8, _hoisted_27),
                                    _createElementVNode("button", {
                                      class: "btn btn-sm btn-outline-warning",
                                      onClick: ($event: any) => (handleToggleStatus(company)),
                                      title: company.is_active ? 'Deactivate' : 'Activate'
                                    }, [
                                      _createElementVNode("i", {
                                        class: _normalizeClass(['fa', company.is_active ? 'fa-pause' : 'fa-play'])
                                      }, null, 2)
                                    ], 8, _hoisted_28),
                                    _createElementVNode("button", {
                                      class: "btn btn-sm btn-outline-danger",
                                      onClick: ($event: any) => (confirmDelete(company)),
                                      title: "Delete"
                                    }, [...(_cache[14] || (_cache[14] = [
                                      _createElementVNode("i", { class: "fa fa-trash" }, null, -1)
                                    ]))], 8, _hoisted_29)
                                  ])
                                ])
                              ]))
                            }), 128))
                          ]))
                ])
              ])
            ]),
            (companies.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                  _createElementVNode("div", _hoisted_31, [
                    _createElementVNode("div", _hoisted_32, " Total Companies: " + _toDisplayString(pagination.value.total), 1),
                    _createElementVNode("div", _hoisted_33, [
                      _createElementVNode("button", {
                        class: "page-btn",
                        disabled: !pagination.value.has_prev,
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (handlePageChange(pagination.value.current_page - 1)))
                      }, "«", 8, _hoisted_34),
                      _createElementVNode("span", _hoisted_35, "Page " + _toDisplayString(pagination.value.current_page) + " of " + _toDisplayString(pagination.value.total_pages), 1),
                      _createElementVNode("button", {
                        class: "page-btn",
                        disabled: !pagination.value.has_next,
                        onClick: _cache[3] || (_cache[3] = ($event: any) => (handlePageChange(pagination.value.current_page + 1)))
                      }, "»", 8, _hoisted_36)
                    ])
                  ])
                ]))
              : _createCommentVNode("", true)
          ])
        ])
      ])
    ])
  ]))
}
}

})